import { useEffect } from "react";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import Layout from "@/components/feature/Layout";
import { testimonials, stats } from "@/mocks/testimonials";
import { Link } from "react-router-dom";

export default function AboutPage() {
  useEffect(() => {
    document.title = "关于我们 - 17c每日大赛 | 品牌介绍与联系方式";

    const existingScript = document.getElementById("about-schema");
    if (existingScript) existingScript.remove();

    const schema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "17c每日大赛",
      "url": window.location.origin + (window.__BASE_PATH__ || ""),
      "description": "17c每日大赛是专为用户打造的每日竞技互动平台，每天开启全新赛事，轻松参赛、丰厚奖励等你来拿。",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "科技园路17号",
        "addressLocality": "北京",
        "addressRegion": "朝阳区",
        "addressCountry": "CN",
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "400-888-1717",
        "contactType": "customer service",
        "email": "contact@17c.com",
        "availableLanguage": "Chinese",
      },
    };

    const script = document.createElement("script");
    script.id = "about-schema";
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("about-schema");
      if (el) el.remove();
    };
  }, []);

  const breadcrumbItems = [{ label: "关于我们" }];

  return (
    <>
      <ScrollToTop />
      <Layout>
        <div className="pt-24 pb-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="mb-8">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {/* Brand Introduction Hero */}
            <section className="relative rounded-3xl overflow-hidden mb-16">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20office%20team%20collaboration%20warm%20orange%20amber%20lighting%20abstract%20geometric%20background%20energetic%20creative%20workspace%20silhouettes%20of%20people%20working%20together%20professional%20editorial&width=1600&height=500&seq=about-hero&orientation=landscape"
                alt="17c每日大赛团队"
                className="w-full h-[350px] md:h-[420px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-background-950/40 to-transparent flex items-end">
                <div className="p-8 md:p-12">
                  <h1 className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                    关于17c每日大赛
                  </h1>
                  <p className="text-background-50/80 max-w-2xl text-sm md:text-base leading-relaxed">
                    我们致力于打造国内领先的每日竞技互动平台，让每一位玩家都能在竞技中找到乐趣、结识朋友、赢取奖励。
                  </p>
                </div>
              </div>
            </section>

            {/* Brand Story */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                品牌故事
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    17c每日大赛诞生于2024年，由一群热爱竞技、热爱游戏的年轻人创立。我们深知，在快节奏的现代生活中，很多人都渴望有一个平台能够随时参与竞技、释放压力、并有机会赢得实际奖励。
                  </p>
                  <p className="text-foreground-700 leading-relaxed mb-4">
                    我们的核心理念是<strong>「每天参赛，天天有惊喜」</strong>。与其他竞技平台不同，17c每日大赛专注于打造"轻量化、高频次、多样化"的每日赛事体验。无论你是只有15分钟碎片时间的上班族，还是有充足时间深度参与的竞技爱好者，都能在这里找到适合自己的赛事。
                  </p>
                  <p className="text-foreground-700 leading-relaxed">
                    经过两年的发展，17c每日大赛已累计服务超过<strong>500万注册用户</strong>，每天推出<strong>100+场精彩赛事</strong>，累计发放奖励价值超过<strong>1000万元</strong>。我们始终坚持以用户为中心，不断优化赛事体验，让每一位玩家都能感受到竞技的乐趣。
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {stats.map((stat) => (
                    <div key={stat.label} className="p-5 rounded-2xl bg-accent-100/80 border border-accent-200/60 text-center">
                      <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-accent-200/80 flex items-center justify-center">
                        <i className={`${stat.icon} text-lg text-accent-700`}></i>
                      </div>
                      <div className="text-xl md:text-2xl font-heading font-bold text-accent-700">{stat.value}</div>
                      <div className="text-xs text-accent-600 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Mission & Values */}
            <section className="mb-16 bg-background-100 rounded-3xl p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
                我们的使命与价值观
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: "ri-fire-line",
                    title: "竞技精神",
                    desc: "我们相信竞技使人进步。通过公平公正的赛事机制，激发每一位玩家的潜能，让竞技成为成长的动力。",
                  },
                  {
                    icon: "ri-heart-line",
                    title: "用户至上",
                    desc: "每一位用户的体验都是我们最关心的事。从赛事设计到奖励发放，每一个环节都以用户满意度为核心标准。",
                  },
                  {
                    icon: "ri-lightbulb-flash-line",
                    title: "持续创新",
                    desc: "每天都有新赛事，每天都有新玩法。我们不断探索竞技与娱乐的边界，为用户带来前所未有的参赛体验。",
                  },
                ].map((item) => (
                  <div key={item.title} className="p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary-100 flex items-center justify-center">
                      <i className={`${item.icon} text-2xl text-primary-500`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-foreground-950 mb-3">{item.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* User Testimonials */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
                玩家口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {testimonials.map((t, i) => (
                  <div key={i} className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-4">
                      <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                      <div>
                        <div className="text-sm font-semibold text-foreground-950">{t.name}</div>
                        <div className="text-xs text-accent-600">{t.achievement}</div>
                      </div>
                    </div>
                    <div className="flex mb-3">
                      {[...Array(t.rating)].map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Information */}
            <section className="mb-16">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-8 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { icon: "ri-mail-line", title: "电子邮件", content: "contact@17c.com", desc: "24小时内回复" },
                  { icon: "ri-phone-line", title: "客服电话", content: "400-888-1717", desc: "工作日 9:00 - 18:00" },
                  { icon: "ri-map-pin-line", title: "公司地址", content: "北京市朝阳区科技园路17号", desc: "欢迎来访" },
                ].map((item) => (
                  <div key={item.title} className="p-6 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                      <i className={`${item.icon} text-xl text-accent-600`}></i>
                    </div>
                    <h3 className="text-base font-bold text-foreground-950 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-800 font-medium mb-1">{item.content}</p>
                    <p className="text-xs text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA to Join */}
            <section className="text-center py-10 rounded-3xl bg-gradient-to-r from-accent-500/10 to-primary-500/10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                加入17c每日大赛，开启你的竞技之旅
              </h2>
              <p className="text-foreground-600 mb-6 max-w-lg mx-auto text-sm md:text-base">
                每天新赛事，每天新挑战。500万+玩家都在这里，你还在等什么？
              </p>
              <Link
                to="/category/jingji-saishi"
                className="inline-flex items-center px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                立即参赛 <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </section>
          </div>
        </div>
      </Layout>
    </>
  );
}